// In-page signals between the live layer (offers, raises; mounted in the shell) and the console screen.

import { useEffect, useRef } from 'react';
import type { EventDetail } from '../api.ts';

export type LiveSignal =
  /** I accepted an offer: the console opens the event (detail already loaded). */
  | { type: 'accepted'; detail: EventDetail }
  /** My active / parked set changed outside the console's own commands. */
  | { type: 'mine-changed' }
  /** A parked event of mine was raised back to me. */
  | { type: 'raised'; eventId: string }
  /** A supervisor reassigned an event to me (the live layer has already said so). */
  | { type: 'assigned'; eventId: string };

const subs = new Set<(s: LiveSignal) => void>();

export function emitLive(s: LiveSignal): void {
  for (const cb of subs) {
    try {
      cb(s);
    } catch (e) {
      console.error('live signal handler failed', e);
    }
  }
}

export function useLiveSignal(handler: (s: LiveSignal) => void): void {
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    const cb = (s: LiveSignal) => ref.current(s);
    subs.add(cb);
    return () => {
      subs.delete(cb);
    };
  }, []);
}
